import { create } from 'zustand';
import { v4 as uuidV4 } from 'uuid';
import { immer } from 'zustand/middleware/immer';
import { clone } from '@/utils';
import { IFileData } from '@/components/Upload/tools';
import { localMemory } from 'localmemory';
import { sessionConfigCache } from './userStore';

export enum EMessageStatus {
  pending = 'pending',
  success = 'success',
  fail = 'fail',
}

export enum EMsgType {
  JoinServer = 0, // 不管
  TextMsgType = 1, //
  CreateSessionMsgType = 4,
  AgentUserJoinSessionMsgType = 5, // 客服接入推送
  HasReadReceiptMsgType = 6,
  KickOffLineMsgType = 7, // 用户已经被服务器踢出
  EndSessionMsgType = 8,
}

export enum EContentType {
  text = 1,
  image = 2,
  video = 3,
  CreateSessionContentType = 4,
  AgentUserJoinSessionContentType = 5,
  EndSessionContentType = 6,
  RankingContentType = 7,
  FaqContentType = 8,
  KnowledgePointContentType = 9,
  KnowledgeAnswerContentType = 10,
  TransferContentType = 11,
  SystemContentType = 12,
  UnsupportedContentType = 13,
  tips = 99,
}

export interface IMessage {
  msgBody: string;
  msgSeq: number;
  msgType: number;
  senderUid: string;
  receiverUid: string;
  status: EMessageStatus;
  createTime: string;
  updateTime: string;
  clientMsgID: string;
  uniqueId: string;
  sendTime: string;
  sessionId: string;
  msgFromTime: 'history' | 'current';
  serverMsgID?: string;
  progress?: number;
  isRead?: boolean;
  isStopUpload?: boolean;
  file?: File;
  thumbnail?: string;
  uploadId?: string;
  fileData?: IFileData;
  title?: string;
  senderNickname?: string;
  senderFaceURL?: string;
  isInitMsg?: boolean; // the message is belongs first history message
  isLockClickUpload?: boolean;
}

export interface ISessionList {
  msgSeq: number;
  sessionId: string;
}

type State = {
  scrollMessage: IMessage[];
  historyMessageList: IMessage[];
  previewImgsList: IMessage[];
  messageList: IMessage[];
  otherMessageList: IMessage[];
  latestSeq: number;
  sessionList: ISessionList[];
  wsMessageList: IMessage[];
  isInitHistory: boolean;
  showPreviewMedia: boolean;
  activePreviewMediaInx: number;
  unRead: number;
};

type Action = {
  setShowPreviewMedia: (showPreviewMedia: boolean) => any;
  setHistoryMessageList: (messageList: IMessage[]) => void;
  setPreviewImgsList: (messageList: IMessage[]) => void;
  setMessageList: (messageList: IMessage[]) => void;
  addMessageList: (msg: IMessage) => void;
  setLatestSeq: (seq: number) => void;
  updateMessageItem: (msg: IMessage) => void;
  addMessageItem: (msg: IMessage) => void;
  updateMessageProgress: (msg: IMessage) => void;
  updateMessageProgress2: (msg: IMessage) => void;
  setScrollMessage: (messageList: IMessage[]) => void;
  setSessionList: (sessionList: ISessionList[]) => void;
  setWsMessageList: (messageList: IMessage[]) => void;
  setIsInitHistory: (isInitHistory: boolean) => void;
  getMediaMsgList: () => IMessage[];
  setActivePreviewMediaInx: (inx: number) => void;
  updateMessageList: (msg: IMessage[]) => void;
  updateMessageUploadId: (msg: IMessage) => void;
  updateMessageUploadIdAndThumbnail: (msg: IMessage) => void;
  updateMessageThumbnail: (msg: IMessage) => void;
  updateIsLockClickUpload: (msg: IMessage, status?: boolean) => void;
  getMsgList: () => IMessage[];
  setUnRead: (unRead: number) => void;
  updateMsgSessionId: (msg: IMessage) => void;
};

const useMessageStore = create<State & Action>()(
  immer<State & Action>((set, get) => ({
    scrollMessage: [],
    historyMessageList: [],
    previewImgsList: [],
    messageList: [],
    otherMessageList: [],
    latestSeq: 0,
    sessionList: [],
    wsMessageList: [],
    isInitHistory: false,
    showPreviewMedia: false,
    activePreviewMediaInx: 0,
    unRead: 0,

    setShowPreviewMedia: (showPreviewMedia: boolean) => set(() => ({ showPreviewMedia })),

    setIsInitHistory: (isInitHistory: boolean) => set(() => ({ isInitHistory })),

    setWsMessageList: (messageList: IMessage[]) => set(() => ({ wsMessageList: messageList })),

    setHistoryMessageList: (messageList: IMessage[]) =>
      set((prev) => {
        return { historyMessageList: [...prev.historyMessageList, ...messageList] };
      }),

    setPreviewImgsList: (messageList: IMessage[]) =>
      set((prev) => {
        return { previewImgsList: [...prev.previewImgsList, ...messageList] };
      }),
    setSessionList: (sessionList: ISessionList[]) => set(() => ({ sessionList })),

    setMessageList: (messageList: IMessage[]) => {
      const cloneMessageList = clone(messageList, true);

      const getSessionRank = (sessionId: string): number => {
        const t = get().sessionList;

        return t.findIndex((item) => {
          return item.sessionId === sessionId;
        });
      };

      let tempNewList = cloneMessageList
        .map((item) => {
          const tempClientMsgId = item.clientMsgID.split('-');
          item.sendTime = tempClientMsgId[tempClientMsgId.length - 1];
          item.uniqueId = tempClientMsgId.slice(0, -1).join('-');
          return item;
        })
        .sort((a, b) => {
          if (a.sessionId === b.sessionId) {
            // 由于服务端处理的时间有延迟,所以要叠加一下serverMsgID
            if (!a.serverMsgID || !b.serverMsgID) {
              // 首次进入的时候，给推送消息，我发的消息没有serverMsgID，但是我的sendTime比他后发的还要大，所以要再加入一层msgSeq作判断
              if (a.msgSeq && b.msgSeq) {
                if (a.msgSeq === b.msgSeq) {
                  return +a.sendTime - +b.sendTime;
                }
                return a.msgSeq - b.msgSeq;
              }
              return +a.sendTime - +b.sendTime;
            }

            if (+a.serverMsgID! === +b.serverMsgID!) {
              return +a.sendTime - +b.sendTime;
            }
            return +a.serverMsgID! - +b.serverMsgID!;
          }

          const sessionA = getSessionRank(a.sessionId);
          const sessionB = getSessionRank(b.sessionId);
          return sessionB - sessionA;
        });

      const lastestMsg = tempNewList?.[tempNewList.length - 1];
      const seq = lastestMsg?.msgSeq;
      console.log('最后的seq...', seq, messageList);

      set(() => ({ latestSeq: seq || 0 }));

      // tempNewList = tempNewList.sort((a, b) => {
      //   if (a.sessionId === b.sessionId) {
      //     return +a.sendTime - +b.sendTime;
      //   }

      //   const sessionA = getSessionRank(a.sessionId);
      //   const sessionB = getSessionRank(b.sessionId);
      //   return sessionB - sessionA;
      // });

      console.log('tempNewList....', tempNewList);
      return set(() => {
        return { messageList: tempNewList };
      });
    },

    updateMessageList: (newMsgList: IMessage[]) => {
      set((state) => {
        for (let i = 0, len = newMsgList.length; i < len; i++) {
          for (let j = 0, jlen = state.messageList.length; j < jlen; j++) {
            const newItem = newMsgList[i];
            const msgItem = state.messageList[j];
            if (newItem.uniqueId === msgItem.uniqueId) {
              state.messageList[j] = newItem;
            }
          }
        }

        state.latestSeq = newMsgList[newMsgList.length - 1].msgSeq;
      });
    },

    /**
     * update item
     * @param msg
     */
    updateMessageItem: (msg: IMessage) => {
      set((state) => {
        for (let i = 0, len = state.messageList.length; i < len; i++) {
          const item = state.messageList[i];

          if (item.uniqueId === msg.uniqueId) {
            state.messageList[i] = msg;
          }
        }

        state.latestSeq = state.messageList[state.messageList.length - 1].msgSeq;
      });
    },

    addMessageItem: (msg: IMessage) => {
      set((state) => {
        const tempMsg = { ...msg };
        tempMsg.msgFromTime = 'current';
        const tempClientMsgId = msg.clientMsgID.split('-');
        tempMsg.sendTime = tempClientMsgId[tempClientMsgId.length - 1];
        tempMsg.uniqueId = tempClientMsgId.slice(0, -1).join('-');

        state.messageList.push(tempMsg);

        state.latestSeq = state.messageList[state.messageList.length - 1].msgSeq;
      });
    },

    /**
     * @param msg
     */
    updateMessageProgress: (msg: IMessage) => {
      set((prev) => {
        for (let i = 0, len = prev.messageList.length; i < len; i++) {
          const item = prev.messageList[i];

          if (item.uniqueId === msg.uniqueId) {
            prev.messageList[i].progress = msg.progress;
            prev.messageList[i].file = msg.file;
            prev.messageList[i].thumbnail = msg.thumbnail;
          }
        }
      });
    },

    /**
     * @param msg
     */
    updateMessageProgress2: (msg: IMessage) => {
      set((prev) => {
        for (let i = 0, len = prev.messageList.length; i < len; i++) {
          const item = prev.messageList[i];

          if (item.uniqueId === msg.uniqueId) {
            prev.messageList[i].progress = msg.progress;
            prev.messageList[i].status = msg.status;
          }
        }
      });
    },

    /**
     * @param msg
     */
    updateMessageUploadId: (msg: IMessage) => {
      set((prev) => {
        for (let i = 0, len = prev.messageList.length; i < len; i++) {
          const item = prev.messageList[i];

          if (item.uniqueId === msg.uniqueId) {
            prev.messageList[i].uploadId = msg.uploadId;
          }
        }
      });
    },

    updateMessageUploadIdAndThumbnail: (msg: IMessage) => {
      set((prev) => {
        for (let i = 0, len = prev.messageList.length; i < len; i++) {
          const item = prev.messageList[i];

          if (item.uniqueId === msg.uniqueId) {
            prev.messageList[i].uploadId = msg.uploadId;
            prev.messageList[i].thumbnail = msg.thumbnail;
          }
        }
      });
    },

    /**
     * @param msg
     */
    updateMessageThumbnail: (msg: IMessage) => {
      set((prev) => {
        for (let i = 0, len = prev.messageList.length; i < len; i++) {
          const item = prev.messageList[i];

          if (item.uniqueId === msg.uniqueId) {
            prev.messageList[i].thumbnail = msg.thumbnail;
          }
        }
      });
    },

    setLatestSeq: (seq: number) => set(() => ({ latestSeq: seq })),

    setScrollMessage: (messageList: IMessage[]) =>
      set((prev) => {
        return { scrollMessage: [...prev.scrollMessage, ...messageList] };
      }),

    setActivePreviewMediaInx: (inx: number) => set(() => ({ activePreviewMediaInx: inx })),

    getMediaMsgList: () => {
      const temp = get().messageList;

      const robotMsg = [];
      for (const item of temp) {
        if (item.status === EMessageStatus.success) {
          const resp = [EContentType.image, EContentType.video].includes(item.msgType);
          if (resp) {
            robotMsg.push(item);
            continue;
          }
        }
        if (item.msgType === EContentType.KnowledgeAnswerContentType) {
          try {
            const t = JSON.parse(item.msgBody);

            t.forEach((mediaItem: { type: number; content: any }, inx: string) => {
              if (mediaItem.type === 1) {
                robotMsg.push({
                  msgBody: mediaItem.content,
                  uniqueId: item.uniqueId + '_' + inx,
                  msgType: EContentType.image,
                });
              }
            });
          } catch (error) {}

          continue;
        }
      }

      return robotMsg;
    },

    getMsgList: () => {
      return get().messageList;
    },

    addMessageList: (msgItem: IMessage) => {
      set((state) => {
        return {
          messageList: [...state.messageList, msgItem],

          latestSeq: state.messageList[state.messageList.length - 1]?.msgSeq || 0,
        };
      });
    },

    setUnRead: (unRead: number) => {
      set(() => ({ unRead }));
    },

    updateIsLockClickUpload: (msg: IMessage, status?: boolean) => {
      set((prev) => {
        for (let i = 0, len = prev.messageList.length; i < len; i++) {
          const item = prev.messageList[i];

          if (item.uniqueId === msg.uniqueId) {
            prev.messageList[i].isLockClickUpload = status ?? !msg.isLockClickUpload;
          }
        }
      });
    },

    updateMsgSessionId(msg: IMessage) {
      set((prev) => {
        for (let i = 0, len = prev.messageList.length; i < len; i++) {
          const item = prev.messageList[i];

          if (item.uniqueId === msg.uniqueId) {
            prev.messageList[i].sessionId = msg.sessionId;
          }
        }
      });
    },
  }))
);

export default useMessageStore;

export const useGetMediaList = () => {
  const messageStore = useMessageStore();
  const mediaList = useMemo(() => {
    const resp = messageStore.getMediaMsgList();
    return resp;
  }, [messageStore.messageList]);

  return {
    mediaList,
  };
};

export const useUserMessage = () => {
  const { getSessionConfig } = sessionConfigCache();
  const { sessionConfig } = getSessionConfig();

  const messageStore = useMessageStore();
  const [newMsgCount, setNewMsgCount] = useState<number>(0);

  // News from others
  const otherMessage = useMemo(() => {
    const other = messageStore.messageList.filter((item) => {
      return item.senderUid !== sessionConfig.uid;
    });

    const unReadMsg = other.filter((item) => {
      return !item.isRead;
    });
    setNewMsgCount(unReadMsg.length);

    return other;
  }, [messageStore.messageList]);

  return {
    otherMessage,
    newMsgCount,
  };
};
